import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  Typography,
  useLocaleFormat,
} from '@eye-able/ui';
import { useEffect, useRef, useState } from 'react';

import type { GovernanceIssueGroupResponse } from '@http/queries/useFetchGovernanceIssueGroups';
import type { UpdatableGovernanceIssueStatus } from '@http/queries/useUpdateGovernanceIssueStatus';
import type {
  GovernanceIssueResponse,
  GovernanceIssuesFilters,
  PolicyViolationLabel,
} from '@http/queries/useFetchGovernanceIssues';
import { useFetchGovernanceIssueGroupIssues } from '@http/queries/useFetchGovernanceIssueGroupIssues';
import type { GovernanceIssuesGroupBy } from '@http/queries/useFetchGovernanceIssueGroups';
import LoadingSpinner from '@components/LoadingSpinner';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { getPriorityTranslationKey } from '../priority';

import IssueCard from './IssueCard';

function GroupIssues({
  group,
  groupBy,
  filters,
  pendingUpdate,
  onStatusChange,
  policyViolationLabels,
}: {
  group: GovernanceIssueGroupResponse;
  groupBy: GovernanceIssuesGroupBy;
  filters: GovernanceIssuesFilters;
  pendingUpdate: { id: number; status: UpdatableGovernanceIssueStatus } | null;
  onStatusChange: (
    issue: GovernanceIssueResponse,
    status: UpdatableGovernanceIssueStatus,
  ) => void;
  policyViolationLabels: Map<number, PolicyViolationLabel>;
}) {
  const { t } = useInlineTranslation();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchGovernanceIssueGroupIssues(groupBy, group.key, filters);
  const issues = data?.pages.flatMap((page) => page.data) ?? [];

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasNextPage) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '300px 0px' },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return (
      <Typography className='eye:text-grey-120'>
        {t('couldNotLoadGroup')}
      </Typography>
    );
  }

  return (
    <>
      {issues.map((issue) => (
        <IssueCard
          key={issue.id}
          issue={issue}
          policyViolationLabel={policyViolationLabels.get(issue.policyId)}
          pendingStatus={
            pendingUpdate?.id === issue.id ? pendingUpdate.status : null
          }
          onStatusChange={onStatusChange}
        />
      ))}
      {issues.length < group.count ? (
        <Typography variant='body-s' className='eye:text-grey-120 eye:italic'>
          {t('showingIssues', {
            shownCount: issues.length,
            totalCount: group.count,
          })}
        </Typography>
      ) : null}
      {hasNextPage ? <div ref={loadMoreRef} className='eye:h-px' /> : null}
      {isFetchingNextPage ? <LoadingSpinner /> : null}
      {hasNextPage ? (
        <Button
          variant='outline'
          size='sm'
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className='eye:self-start'
        >
          {t('loadMoreIssues')}
        </Button>
      ) : null}
    </>
  );
}

function GroupedIssueList({
  groups,
  groupBy,
  filters,
  pendingUpdate,
  onStatusChange,
  policyViolationLabels,
}: {
  groups: GovernanceIssueGroupResponse[];
  groupBy: GovernanceIssuesGroupBy;
  filters: GovernanceIssuesFilters;
  pendingUpdate: { id: number; status: UpdatableGovernanceIssueStatus } | null;
  onStatusChange: (
    issue: GovernanceIssueResponse,
    status: UpdatableGovernanceIssueStatus,
  ) => void;
  policyViolationLabels: Map<number, PolicyViolationLabel>;
}) {
  const { t } = useInlineTranslation();
  const { formatNumber } = useLocaleFormat();
  // Groups start collapsed so opening one starts only that group's independent
  // paginated request; a large group list therefore does not fan out into one
  // request per header.
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  return (
    <Accordion
      type='multiple'
      value={openKeys}
      onValueChange={setOpenKeys}
      className='eye:flex eye:flex-col eye:gap-2xs'
    >
      {groups.map((group) => {
        const isOpen = openKeys.includes(group.key);

        return (
          <AccordionItem
            key={group.key}
            value={group.key}
            className='eye:border-b-0'
          >
            <Card>
              <AccordionTrigger
                chevronPosition='left'
                className='eye:gap-2xs eye:px-s eye:hover:no-underline eye:group'
              >
                <span className='eye:flex eye:min-w-0 eye:flex-1 eye:items-center eye:gap-2xs eye:break-all'>
                  <span className='eye:group-hover:underline'>
                    {groupBy === 'priority'
                      ? t(
                          getPriorityTranslationKey(
                            group.key as 'low' | 'medium' | 'high',
                          ),
                        )
                      : (group.label ?? t('unassigned'))}
                  </span>
                </span>
                <span className='eye:body-s eye:shrink-0 eye:font-normal eye:text-grey-120'>
                  {t('issueCount', {
                    count: group.count,
                    formattedCount: formatNumber(group.count),
                  })}
                </span>
              </AccordionTrigger>
            </Card>
            <AccordionContent className='eye:mb-0 eye:flex eye:flex-col eye:gap-2xs eye:pt-2xs'>
              {isOpen ? (
                <GroupIssues
                  group={group}
                  groupBy={groupBy}
                  filters={filters}
                  pendingUpdate={pendingUpdate}
                  onStatusChange={onStatusChange}
                  policyViolationLabels={policyViolationLabels}
                />
              ) : null}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

export default GroupedIssueList;
